import React, { useState, useEffect, useCallback } from 'react';
import { FaCalendarAlt, FaClock, FaMoneyBillWave, FaUser, FaExclamationCircle, FaMapMarkerAlt } from 'react-icons/fa';
import { toast } from 'react-toastify';
import FetchWithGif from '../../../components/Reutilizables/FetchWithGif';
import { getCuotasPendientes, getDescripcionPeriodo } from '../../../js/cuotasPendientesService';

const CuotasPendientesUI = () => {
  const [cuotas, setCuotas] = useState([]);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [descripcionPeriodo, setDescripcionPeriodo] = useState({
    esDiaSiguiente: false,
    esLunes: false,
    texto: 'hoy',
    textoCompleto: 'Cuotas que vencen hoy'
  });

  const cargarCuotasPendientes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await getCuotasPendientes();

      setCuotas(data.data);
      setMeta(data.meta);
      setDescripcionPeriodo(getDescripcionPeriodo(data.meta));
    } catch (err) {
      console.error('Error al cargar cuotas pendientes:', err);
      setError(err.message || 'No se pudieron cargar las cuotas pendientes');
      toast.error(err.message || 'Error al cargar cuotas pendientes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargarCuotasPendientes();
  }, [cargarCuotasPendientes]);

  const formatearFecha = (fecha) => {
    if (!fecha) return '-';
    const date = new Date(`${fecha}T00:00:00`);
    return date.toLocaleDateString('es-PE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatearMoneda = (monto) => {
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'PEN',
      minimumFractionDigits: 2
    }).format(monto);
  };

  const obtenerMontoAPagar = (cuota) =>
    cuota?.monto_a_pagar ?? cuota?.saldo_pendiente ?? cuota?.monto_cuota ?? 0;

  const coordenadaValida = (valor, minimo, maximo) => {
    const coordenada = Number(valor);
    return Number.isFinite(coordenada) && coordenada >= minimo && coordenada <= maximo;
  };

  const ubicacionRegistrada = (latitud, longitud) => {
    const lat = Number(latitud);
    const lng = Number(longitud);

    return coordenadaValida(lat, -90, 90)
      && coordenadaValida(lng, -180, 180)
      && !(lat === 0 && lng === 0);
  };

  const mostrarUbicacionNoRegistrada = () => {
    const mensaje = 'No hay ubicación registrada para esta dirección.';
    toast.warning(mensaje, { toastId: 'ubicacion-no-registrada' });
  };

  const abrirUbicacionGoogleMaps = (ubicacion) => {
    const latitud = ubicacion?.latitud;
    const longitud = ubicacion?.longitud;

    if (!ubicacionRegistrada(latitud, longitud)) {
      mostrarUbicacionNoRegistrada();
      return;
    }

    window.open(
      `https://www.google.com/maps?q=${Number(latitud)},${Number(longitud)}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  if (loading) {
    return <FetchWithGif />;
  }

  return (
    <div className="space-y-6">
      <section className="ui-card relative overflow-hidden px-6 py-7 md:px-8">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_88%_10%,rgba(217,44,52,0.12),transparent_38%)]" />
        <div className="relative">
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-red-600">Gestión asesor</p>
          <h1 className="mt-2 text-2xl font-semibold text-slate-900 md:text-3xl">Cuotas pendientes</h1>
          <p className="mt-2 text-sm text-slate-600">
            Seguimiento de cuotas por vencer para el período operativo actual.
          </p>
        </div>
      </section>

      <section className="ui-card p-5 md:p-6">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Periodo</p>
          <p className="text-sm font-medium text-slate-700">
            {descripcionPeriodo.textoCompleto}: {formatearFecha(meta.fecha)}
          </p>
        </div>
      </section>

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Total cuotas</p>
              <p className="text-3xl font-semibold text-slate-900">{meta.total_cuotas || 0}</p>
            </div>
            <div className="rounded-xl bg-red-100 p-4 text-red-600">
              <FaCalendarAlt className="h-7 w-7" />
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Monto total a pagar</p>
              <p className="text-3xl font-semibold text-slate-900">{formatearMoneda(meta.monto_total || 0)}</p>
            </div>
            <div className="rounded-xl bg-emerald-100 p-4 text-emerald-600">
              <FaMoneyBillWave className="h-7 w-7" />
            </div>
          </div>
        </div>
      </section>

      {error && (
        <section className="rounded-xl border border-red-200 bg-red-50 p-4 text-red-700">
          <div className="flex items-start gap-3">
            <FaExclamationCircle className="mt-0.5 h-5 w-5" />
            <div>
              <h3 className="text-sm font-semibold">Error al cargar los datos</h3>
              <p className="mt-1 text-sm">{error}</p>
              <button
                onClick={cargarCuotasPendientes}
                className="mt-3 rounded-lg border border-red-300 px-3 py-1.5 text-xs font-semibold text-red-700 transition hover:bg-red-100"
              >
                Reintentar
              </button>
            </div>
          </div>
        </section>
      )}

      {!error && !loading && cuotas.length === 0 && (
        <section className="ui-card p-12 text-center">
          <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-slate-100">
            <FaCalendarAlt className="h-10 w-10 text-slate-400" />
          </div>
          <h3 className="text-xl font-semibold text-slate-900">No hay cuotas pendientes para {descripcionPeriodo.texto}</h3>
          <p className="mx-auto mt-2 max-w-xl text-sm text-slate-600">
            No se encontraron cuotas con vencimiento para {descripcionPeriodo.texto}. Todas las cuotas están al día.
          </p>
        </section>
      )}

      {!error && cuotas.length > 0 && (
        <section className="ui-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-[980px] table-fixed text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">
                  <th className="px-4 py-4 text-center" style={{ width: '9%' }}>N° Cuota</th>
                  <th className="px-4 py-4 text-left" style={{ width: '27%' }}>Cliente</th>
                  <th className="px-4 py-4 text-center" style={{ width: '17%' }}>Monto a pagar</th>
                  <th className="px-4 py-4 text-center" style={{ width: '17%' }}>Fecha de vencimiento</th>
                  <th className="px-4 py-4 text-center" style={{ width: '13%' }}>Hora cobranza</th>
                  <th className="px-4 py-4 text-center" style={{ width: '17%' }}>Ubicación</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {cuotas.map((cuota) => (
                  <tr key={cuota.id_cuota} className="transition hover:bg-slate-50">
                    <td className="px-4 py-4 text-center">
                      <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-red-100 text-sm font-bold text-red-700">
                        {cuota.numero_cuota}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100">
                          <FaUser className="text-slate-500" />
                        </div>
                        <div className="truncate text-sm font-semibold text-slate-900">{cuota.cliente}</div>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <div className="text-base font-semibold text-slate-900">{formatearMoneda(obtenerMontoAPagar(cuota))}</div>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-red-100">
                          <FaCalendarAlt className="text-sm text-red-600" />
                        </div>
                        <div className="text-sm font-medium text-slate-900">{formatearFecha(cuota.fecha_vencimiento)}</div>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100">
                          <FaClock className="text-sm text-slate-600" />
                        </div>
                        <div className="text-sm font-medium text-slate-900">{cuota.hora_cobranza || '--:--'}</div>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <div className="flex items-center justify-center">
                        <button
                          type="button"
                          onClick={() => abrirUbicacionGoogleMaps(cuota.ubicacion_correspondencia)}
                          className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-700 transition hover:border-slate-300 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-200"
                        >
                          <FaMapMarkerAlt className="h-3.5 w-3.5" />
                          Correspondencia
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
};

export default CuotasPendientesUI;
